import { useMemo, type ReactNode } from 'react';
import type { UniqueIdentifier } from '@dnd-kit/abstract';
import type { DropAnimation } from '@dnd-kit/dom';
import { DragOverlay } from '@dnd-kit/react';

import { memoGeneric } from '@/js/libs/memo/index.ts';

import { Row } from '../components/index.ts';
import { dataTableGroup } from '../constants.ts';
import { renderDndColumns } from './renderDndColumns.tsx';
import { OverlayGrid, OverlayWrapper } from './styled.ts';
import type { DndColumn, GetRowId, GroupedItems, UseDndOverlayProps } from './types.ts';

const OVERLAY_DROP_ANIMATION: DropAnimation = { duration: 200 };

// Strip the border-top that Table.Wrapper's `& > * + *` rule otherwise applies
// to this overlay div. The lib sizes the overlay to the full source row width,
// which would make that 1px line extend past the visible table when there's
// horizontal scroll.
const OVERLAY_STYLE = { borderTop: 'none' } as const;

function flattenItems<T>(items: T[] | GroupedItems<T>): T[] {
  return Array.isArray(items) ? items : Object.values(items).flat();
}

export interface DndOverlayProps<T> extends UseDndOverlayProps {
  /** Active row source — a flat list or grouped lists; resolved by id. */
  items: T[] | GroupedItems<T>;
  /**
   * Maps an item to the `UniqueIdentifier` it was registered with on its `Table.DndRow` — the two MUST
   * produce the same id, otherwise the active row can't be resolved and the overlay renders no preview.
   * Pass a referentially stable function (module scope or `useCallback`): it backs the id→item memo and is
   * a prop on this memoized component, so a fresh inline arrow each render rebuilds the map and defeats the
   * memoization the per-frame lookup relies on.
   */
  getId: GetRowId<T>;
  /** Render the dragged row's content. Falls back to the `columns` config when omitted. */
  renderItem?: (item: T) => ReactNode;
  /** Show the group indicator on the preview for items that belong to a visual group. */
  isItemGrouped?: (item: T) => boolean;
  columns?: DndColumn[];
  /** Extra props forwarded to each column's component when falling back to the `columns` config. */
  itemProps?: Record<string, unknown>;
}

export const DndOverlay = memoGeneric(function DndOverlay<T>({
  items,
  getId,
  columns,
  renderItem,
  isItemGrouped,
  gridColumns,
  scrollLeft,
  viewportWidth,
  itemProps,
}: DndOverlayProps<T>) {
  // The DragOverlay render callback fires on every drag frame, so resolve the active item via a
  // prebuilt id→item map (O(1)) instead of flattening + scanning the list each time.
  const itemsById = useMemo(() => {
    const map = new Map<UniqueIdentifier, T>();
    for (const item of flattenItems(items)) map.set(getId(item), item);
    return map;
  }, [items, getId]);

  return (
    <DragOverlay dropAnimation={OVERLAY_DROP_ANIMATION} style={OVERLAY_STYLE}>
      {(source) => {
        const activeItem = source ? itemsById.get(source.id) : undefined;
        if (!activeItem) return null;
        // Mirror the table's group context so GroupIndicator (which keys off [data-table-group]) renders on the preview.
        const groupAttr = isItemGrouped?.(activeItem) ? dataTableGroup : undefined;
        return (
          <OverlayWrapper $scrollLeft={scrollLeft} $viewportWidth={viewportWidth} {...groupAttr}>
            <OverlayGrid $columns={gridColumns} $scrollLeft={scrollLeft}>
              {renderItem ? renderItem(activeItem) : <Row>{renderDndColumns(columns ?? [], { item: activeItem, ...itemProps })}</Row>}
            </OverlayGrid>
          </OverlayWrapper>
        );
      }}
    </DragOverlay>
  );
});
